/**
 * The interface boundary between Bridgeway and the real Firefly image-editor
 * web-component bundle. Bridgeway depends ONLY on these interfaces, never on
 * `@adobe/data` or any firefly-platform internal. The real bundle is loaded at
 * runtime by a `FireflyEditorLoader` (see `loadFireflyEditor.ts`); tests inject
 * a fake loader. This keeps the whole feature rip-out-able (delete the folder)
 * and lets every Bridgeway-side piece be validated before the FFP bundle exists.
 *
 * The bundle entry (`firefly-image-editor/lib/firefly-image-editor-entry.ts`)
 * declares matching shapes on its side. The duplication is intentional and
 * unavoidable: that entry compiles inside the firefly-platform checkout with no
 * path back into this app, and this app loads the bundle by URL at runtime, so
 * neither end can import the other. The two declarations are kept in sync by hand.
 */

/**
 * Image-editing tools proven to work on a Bridgeway IMS token (`firefly_fair_use`,
 * no paywall). `instruct` is the bundle's internal prompt mode; it stays in the
 * union because the bundle still understands it, but Bridgeway no longer surfaces
 * it as a usable tool (see {@link DEFAULT_ENABLED_TOOLS}). `upscale` is
 * intentionally excluded — it returns `403 invalid_ims_scope` on a Bridgeway token.
 */
export type FireflyTool =
	| 'instruct'
	| 'fill'
	| 'remove'
	| 'expand'
	| 'remove_background';

/**
 * Day-1 enabled tool set: direct-manipulation tools only. `instruct` (prompt) is
 * deliberately excluded — prompt-style editing now happens in chat (the "Vibe edit"
 * rail button seeds the composer and the normal edit_image round-trip does the work),
 * so the modal is direct-manipulation only. No upscale (it 403s on a Bridgeway token).
 */
export const DEFAULT_ENABLED_TOOLS: readonly FireflyTool[] = [
	'fill',
	'remove',
	'expand',
	'remove_background',
] as const;

/** Environment for Colligo endpoint selection inside the editor bundle. */
export type FireflyEditorEnvironment = 'production' | 'stage' | 'development';

/** One additional user-attached reference image (base64, no `data:` prefix). */
export interface BackendExtraReference {
	imageBase64: string;
	mime: string;
}

/** Request the editor hands to the backend prompt-generation callback. */
export interface BackendGenerateRequest {
	prompt: string;
	/** base64 (no `data:` prefix) of the edited reference image bytes (the source). */
	referenceImageBase64: string;
	referenceImageMime: string;
	/**
	 * Additional reference images the user attached in the prompt input field,
	 * beyond the source image. Forwarded so the edit actually uses them — the
	 * editor's own model-support filter would otherwise silently drop them.
	 */
	extraReferences?: BackendExtraReference[];
	size?: { width: number; height: number };
}
/** Result the backend prompt-generation callback returns to the editor. */
export interface BackendGenerateResult {
	/** base64 (no `data:` prefix) of the generated image bytes. */
	imageBase64: string;
	mime: string;
}

/**
 * Config Bridgeway hands to the loader to construct an editor instance.
 * Bridgeway owns every field here; the loader (FFP-side wiring) translates it
 * into a real `createFFPHostService({ authentication, environment })` + a no-op
 * `commerceData` so no Adobe paywall can ever appear.
 */
export interface FireflyImageEditorConfig {
	/** DOM node the editor element mounts into. */
	container: HTMLElement;
	/**
	 * Live getter for the current IMS access token. Called per request by the
	 * editor's auth seam so a mid-session token refresh is picked up and an
	 * expired token surfaces as a 401 rather than a stale capture.
	 */
	getAccessToken: () => string | null;
	/** Selects the Colligo endpoint set (prod/stage/dev). */
	environment: FireflyEditorEnvironment;
	/** Tools to enable. Defaults to {@link DEFAULT_ENABLED_TOOLS}. */
	enabledTools?: readonly FireflyTool[];
	/**
	 * When set, PROMPT (instruct) generation is proxied through this callback
	 * (Bridgeway backend + service token w/ `firefly_api` scope) instead of the
	 * editor calling Colligo directly — the browser token lacks the generate
	 * scope. Fill / remove / expand / remove-bg are unaffected.
	 */
	generateImageViaBackend?: (
		req: BackendGenerateRequest,
	) => Promise<BackendGenerateResult>;
}

/**
 * Handle to a live editor instance. This is the entire surface Bridgeway uses to
 * drive the editor: open an image (by URL — no BlobRef crosses the module
 * boundary), read the edited result as raw bytes, observe readiness, tear down.
 */
export interface FireflyImageEditorHandle {
	/**
	 * Load an image into the editor by URL. The editor fetches the URL and
	 * builds its own internal BlobRef (via `openFromExternal` presignedUrl),
	 * so Bridgeway passes only a string.
	 */
	openImageFromUrl(url: string): Promise<void>;
	/**
	 * Resolve the currently-active edited image as a Blob, or `null` if the user
	 * made no edit (closed without generating a result).
	 */
	getEditedImageBlob(): Promise<Blob | null>;
	/**
	 * Register a callback fired once the editor UI has rendered and is
	 * interactive. Returns an unsubscribe function. If already ready, the
	 * callback is invoked on the next microtask.
	 */
	onReady(callback: () => void): () => void;
	/** Destroy the instance and release all held resources (blobs, listeners). */
	destroy(): void;
}

/**
 * Lazily constructs an editor instance. The production implementation injects a
 * `<script type="module">` tag for the FFP bundle served same-origin from
 * `public/editor-assets/`; tests inject a fake. Rejects if the bundle fails to
 * load so the modal can show an error instead of a white screen.
 */
export type FireflyEditorLoader = (
	config: FireflyImageEditorConfig,
) => Promise<FireflyImageEditorHandle>;
